import React from 'react';
import { Box, Typography } from '@mui/material';
import { margin, styled } from '@mui/system';
import CheckIcon from '@mui/icons-material/Check';

// Color definitions based on the project's design system
const colors = {
  primary: {
    main: '#FFA525',
    contrastText: '#ffffff',
  },
  error: {
    main: '#C0c0c0',
  },
  neutral: {
    inactive: '#8C8C8C',
    disabled: '#8C8C8C',
    text: '#ffffff',
  },
  text: {
    primary: '#000000',
    secondary: '#666666',
    disabled: '#ffffff',
  }
};

// Styled Components
const StepperContainer = styled(Box)(({ theme, orientation = 'horizontal', type }) => ({
  display: 'flex',
  flexDirection: orientation === 'horizontal' ? 'row' : 'column',
  alignItems: (orientation === 'horizontal' || type === 'vertical-left') ? 'center' : 'stretch',
  gap: orientation === 'horizontal' ? '16px' : '8px',
  width: '100%',
}));

const StepContainer = styled(Box)(({ theme, orientation = 'horizontal' }) => ({
  display: 'flex',
  flexDirection: orientation === 'horizontal' ? 'column' : 'row',
  alignItems: 'center',
  gap: orientation === 'horizontal' ? '8px' : '12px',
  flex: orientation === 'horizontal' ? 1 : 'none',
  position: 'relative',
}));

const StepCircle = styled(Box)(({ 
  status = 'inactive', 
  size = 'medium',
  variant = 'default'
}) => {
  const sizeMap = {
    small: { width: 24, height: 24, fontSize: '12px' , },
    medium: { width: 32, height: 32, fontSize: '14px' },
    large: { width: 40, height: 40, fontSize: '16px' }
  };

  const statusStyles = {
    inactive: {
      backgroundColor: colors.neutral.disabled,
      color: colors.text.disabled,
    },
    active: {
      backgroundColor: colors.primary.main,
      color: '#ffffff',
    },
    completed: {
      backgroundColor: colors.primary.main,
      color: '#ffffff',
    },
    error: {
      backgroundColor: '#F44336',
      color: '#ffffff',
    },
    disabled: {
      backgroundColor: colors.neutral.disabled,
      color: colors.text.disabled,
    }
  };

  const variantStyles = variant === 'outlined' ? {
    backgroundColor: 'transparent',
    color: status === 'active' ? colors.primary.main : colors.text.secondary,
    border: `2px solid ${status === 'active' ? colors.primary.main : colors.neutral.inactive}`,
    ...(status === 'completed' && {
      backgroundColor: 'transparent',
      color: colors.primary.main,
      border: `2px solid ${colors.primary.main}`,
    })
  } : {};

  return {
    ...sizeMap[size],
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    transition: 'all 0.2s ease-in-out',
    cursor: status === 'disabled' ? 'not-allowed' : 'pointer',
    ...statusStyles[status],
    ...variantStyles,
  };
});

const StepLabel = styled(Typography)(({ 
  status = 'inactive',
  size = 'medium' 
}) => {
  const sizeMap = {
    small: { fontSize: '12px', lineHeight: '16px' },
    medium: { fontSize: '14px', lineHeight: '20px' },
    large: { fontSize: '16px', lineHeight: '24px' }
  };

  const statusStyles = {
    inactive: { color: colors.text.secondary },
    active: { color: colors.text.primary, fontWeight: 600 },
    completed: { color: colors.text.primary, fontWeight: 500 },
    error: { color: '#F44336', fontWeight: 500 },
    disabled: { color: colors.neutral.disabled, opacity: 0.6 }
  };

  return {
    ...sizeMap[size],
    textAlign: 'center',
    maxWidth: '120px',
    ...statusStyles[status],
    transition: 'all 0.2s ease-in-out',
  };
});

const StepConnector = styled(Box)(({ 
  status = 'inactive',
  orientation = 'horizontal',
  connectorStyle = 'solid',
  type,
  size = 'medium'
}) => {
  const isCompleted = status === 'completed';
  const isActive = status === 'active';
  const marginMap = {
    small: 12,
    medium: 16,
    large: 20
  };
  const connectorOffset = marginMap[size];

  const baseStyles = {
    backgroundColor: isCompleted || isActive ? colors.primary.main : colors.neutral.inactive,
    transition: 'all 0.3s ease-in-out',

  };

  if (orientation === 'horizontal') {
    return {
      ...baseStyles,
      height: connectorStyle === 'thick' ? '4px' : '2px',
      flex: 1,
      minWidth: '24px',
      borderRadius: '2px',
      ...(connectorStyle === 'dashed' && {
        background: `repeating-linear-gradient(
          to right,
          ${isCompleted || isActive ? colors.primary.main : colors.neutral.inactive} 0px,
          ${isCompleted || isActive ? colors.primary.main : colors.neutral.inactive} 8px,
          transparent 8px,
          transparent 16px
        )`,
        backgroundColor: 'transparent',
      })
    };
  } else {
    return {
      ...baseStyles,
      width: connectorStyle === 'thick' ? '4px' : '2px',
      minHeight: '32px',
      borderRadius: '2px',
      marginLeft: type === 'vertical-left' ? 0 : `${connectorOffset}px`,
      // marginLeft: type === 'vertical-left' ? '0' :  '15px',
      ...(connectorStyle === 'dashed' && {
        background: `repeating-linear-gradient(
          to bottom,
          ${isCompleted || isActive ? colors.primary.main : colors.neutral.inactive} 0px,
          ${isCompleted || isActive ? colors.primary.main : colors.neutral.inactive} 8px,
          transparent 8px,
          transparent 16px
        )`,
        backgroundColor: 'transparent',
      })
    };
  }
});

const StepSubLabel = styled(Typography)(({ 
  status = 'inactive',
  size = 'medium' 
}) => {
  const sizeMap = {
    small: { fontSize: '10px', lineHeight: '14px' },
    medium: { fontSize: '12px', lineHeight: '16px' },
    large: { fontSize: '14px', lineHeight: '18px' }
  };

  return {
    ...sizeMap[size],
    color: colors.text.secondary,
    textAlign: 'center',
    maxWidth: '120px',
    opacity: status === 'disabled' ? 0.6 : 1,
    marginTop: '2px',
  };
});

const CustomStepper = ({
    type = 'horizontal-left',
    steps = [],
    activeStep = 0,
    orientation = 'horizontal',
    size = 'medium',
    variant = 'default',
    connectorStyle = 'solid',
    showSubLabels = false,
    allowClickNavigation = false,
    onStepClick,
    alignment = 'left',
    sx = {},
    ...props
}) => {
  const handleStepClick = (stepIndex, step) => {
    if (allowClickNavigation && !step.disabled && onStepClick) {
      onStepClick(stepIndex, step);
    }
  };

  const getStepStatus = (stepIndex, step) => {
    if (step.disabled) return 'disabled';
    if (step.error) return 'error';
    if (stepIndex < activeStep) return 'completed';
    if (stepIndex === activeStep) return 'active';
    return 'inactive';
  };

  const getConnectorStatus = (stepIndex) => {
    if (stepIndex < activeStep) return 'completed';
    if (stepIndex === activeStep) return 'active';
    return 'inactive';
  };

  const renderStepContent = (step, stepIndex, status) => {
    const isCompleted = status === 'completed';
    const showCheckIcon = isCompleted && !step.customIcon;

    return (
      <StepCircle
        status={status}
        size={size}
        variant={variant}
        onClick={() => handleStepClick(stepIndex, step)}
        sx={{
          cursor: allowClickNavigation && !step.disabled ? 'pointer' : 'default',
        }}
      >
        {showCheckIcon ? (
          <CheckIcon sx={{ fontSize: size === 'small' ? 14 : size === 'large' ? 20 : 16 }} />
        ) : step.customIcon ? (
          step.customIcon
        ) : (
          stepIndex + 1
        )}
      </StepCircle>
    );
  };

  return (
    <StepperContainer orientation={orientation} type={type} sx={sx} {...props}>
      {steps.map((step, stepIndex) => {
        const status = getStepStatus(stepIndex, step);
        const isLastStep = stepIndex === steps.length - 1;

        return (
          <React.Fragment key={step.id || stepIndex}>
            <StepContainer orientation={orientation} type={type}>
                <Box sx={{ display: 'flex', flexDirection: ( type === 'horizontal-left' || type === 'vertical-center' ) ? 'row': 'column' , alignItems: (type === 'vertical-center') ? 'start' : 'center', gap: orientation === 'horizontal' ? '8px' : '12px' }}>
                    {renderStepContent(step, stepIndex, status)}
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: (type === 'horizontal-left' || type === 'vertical-center')  ? 'flex-start' : alignment === 'center' ? 'center' : 'flex-end',
                        gap: '2px'
                    }}>
                        <StepLabel status={status} size={size}>
                            {step.label}
                        </StepLabel>
                        
                        {showSubLabels && step.subLabel && (
                        <StepSubLabel status={status} size={size}>
                            {step.subLabel}
                        </StepSubLabel>
                        )}
                    </Box>
                </Box>
            </StepContainer>

            {!isLastStep && (
              <StepConnector
                status={getConnectorStatus(stepIndex)}
                orientation={orientation}
                connectorStyle={connectorStyle}
                type={type}
                size={size}
              />
            )}
          </React.Fragment>
        );
      })}
    </StepperContainer>
  );
};

export default CustomStepper;
